import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { factorService } from '../../services/api';

const DriverTestStep = ({ project, factors, onDataChange }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const handleDriverTest = async (factorId, testType, value) => {
    setLoading(true);
    try {
      const updateData = {};
      updateData[testType] = value;
      
      await factorService.updateDriverTest(factorId, updateData);
      onDataChange();
    } catch (error) {
      toast.error(t('factors.driverTest.updateError'));
      console.error('Error updating driver test:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTestOptions = (testType) => {
    // Les opcions ja no porten `status: 'pass' | 'fail'`. Una resposta no és
    // correcta ni incorrecta: descriu el factor. Vegeu `veredicte`.
    switch (testType) {
      case 'certainty_test':
        return [
          { value: 'uncertain', label: t('factors.driverTest.uncertain') },
          { value: 'certain', label: t('factors.driverTest.certain') }
        ];
      case 'significance_test':
        return [
          { value: 'significant', label: t('factors.driverTest.significant') },
          { value: 'insignificant', label: t('factors.driverTest.insignificant') }
        ];
      case 'independence_test':
        return [
          { value: 'independent', label: t('factors.driverTest.independent') },
          { value: 'dependent', label: t('factors.driverTest.dependent') }
        ];
      default:
        return [];
    }
  };

  /**
   * Què ha resultat ser aquest factor.
   *
   * Abans aquí hi havia «Driver» o «Falla», i «Falla» era una paraula
   * equivocada: convertia el test en un examen que el factor aprova o suspèn.
   * El mètode no fa això. Un factor **cert** no és un factor dolent, és una
   * **tendència** —saps cap a on va, i per això fa de context i no
   * d'incertesa—; un **insignificant** simplement no mou l'agulla en aquest
   * cas; i un **dependent** no ha fallat res: és un **factor secundari** i
   * torna a sortir a la passa 5, seguint el driver de qui depèn.
   *
   * Dir-ne «falla» ensenya que t'has equivocat quan el que ha passat és que
   * has classificat bé. Cada resultat té nom i té lloc.
   *
   * Es calcula a partir de les tres respostes i no de
   * `factor.disqualification_reason`, que arriba del servidor en anglès i
   * s'havia de fer coincidir per text.
   */
  const veredicte = (factor) => {
    if (factor.is_driver) return 'driver';
    if (factor.certainty_test === 'certain') return 'trend';
    if (factor.significance_test === 'insignificant') return 'marginal';
    if (factor.independence_test === 'dependent') return 'secondary';
    return 'pending';
  };

  const isTestDisabled = (factor, testType) => {
    if (testType === 'significance_test') {
      return factor.certainty_test === 'certain';
    }
    if (testType === 'independence_test') {
      return factor.certainty_test === 'certain' || factor.significance_test === 'insignificant';
    }
    return false;
  };

  const getDisabledReason = (factor, testType) => {
    if (testType === 'significance_test' && factor.certainty_test === 'certain') {
      return t('factors.driverTest.cannotProceedCertain');
    }
    if (testType === 'independence_test') {
      if (factor.certainty_test === 'certain') {
        return t('factors.driverTest.cannotProceedCertain');
      }
      if (factor.significance_test === 'insignificant') {
        return t('factors.driverTest.cannotProceedInsignificant');
      }
    }
    return null;
  };

  // Aquí hi havia `getTranslatedDisqualificationReason`, que agafava el motiu
  // que el servidor desa en anglès ('Factor is certain', 'Factor is
  // dependent'…) i el feia coincidir per text amb una traducció. Ja no cal:
  // el veredicte es dedueix de les tres respostes, que és d'on surt el motiu
  // en primer lloc, i així no depèn de com estigui escrita una frase al
  // servidor.

  const getTranslatedCategory = (category) => {
    // Map categories to translation keys
    const categoryMap = {
      'political': t('factors.pestel.political'),
      'economic': t('factors.pestel.economic'),
      'social': t('factors.pestel.social'),
      'technological': t('factors.pestel.technological'),
      'environmental': t('factors.pestel.environmental'),
      'legal': t('factors.pestel.legal'),
      'strengths': t('factors.swot.strengths'),
      'weaknesses': t('factors.swot.weaknesses'),
      'opportunities': t('factors.swot.opportunities'),
      'threats': t('factors.swot.threats'),
      'identify': t('factors.cyber.identify'),
      'protect': t('factors.cyber.protect'),
      'detect': t('factors.cyber.detect'),
      'respond': t('factors.cyber.respond'),
      'recover': t('factors.cyber.recover')
    };
    
    return categoryMap[category] || category; // Fallback to original text if not found
  };

  const renderTestButton = (factor, testType, option) => {
    const isSelected = factor[testType] === option.value;
    const isDisabled = isTestDisabled(factor, testType);
    const disabledReason = getDisabledReason(factor, testType);
    
    return (
      <button
        key={option.value}
        onClick={() => !isDisabled && handleDriverTest(factor.id, testType, option.value)}
        disabled={isDisabled || loading}
        title={isDisabled ? disabledReason : ''}
        /* Els botons són neutres. Abans el que mantenia el factor viu sortia
           verd i l'altre vermell, i això és el que feia que una resposta
           semblés bona i l'altra dolenta. Qui explica el resultat és la
           columna de la dreta, que diu què és el factor. */
        className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
          isSelected
            ? 'border-accent-600 bg-accent-50 text-accent-800'
            : isDisabled
            ? 'cursor-not-allowed border-neutral-200 bg-neutral-50 text-neutral-400'
            : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50'
        }`}
      >
        {isSelected && (
          <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
        {option.label}
      </button>
    );
  };

  // Sort factors consistently by creation order (id) or name to prevent jumping around
  const sortedFactors = [...factors].sort((a, b) => {
    // First sort by ID if available (creation order)
    if (a.id && b.id) {
      return a.id - b.id;
    }
    // Fallback to alphabetical by name
    return a.name.localeCompare(b.name);
  });

  const qualifiedFactors = factors.filter(f => f.is_driver);
  // Tot el que ja té veredicte i no és driver. Es mira el veredicte i no
  // `disqualification_reason`, que arriba en anglès del servidor.
  const altresFactors = factors.filter(f => !f.is_driver && veredicte(f) !== 'pending');

  return (
    <div className="space-y-6">
      {factors.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto bg-neutral-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-neutral-900 mb-2">
            {t('factors.driverTest.noFactorsToTest')}
          </h3>
          <p className="text-neutral-600">
            {t('factors.driverTest.addFactorsFirst')}
          </p>
        </div>
      ) : (
        <>
          {/* Compact Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium text-neutral-900">{t('factors.driverTest.testingMatrix')}</h3>
                <button
                  onClick={() => setShowHelpModal(true)}
                  className="w-5 h-5 rounded-full bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center text-neutral-500 hover:text-neutral-700 transition-colors"
                  title={t('factors.driverTest.showHelp')}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-neutral-600">
                  {t('factors.driverTest.factorsSummary', { total: sortedFactors.length, qualified: qualifiedFactors.length })}
                </span>
              </div>
            </div>
          </div>

          {/* Testing Grid */}
          <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      {t('factors.driverTest.factor')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      {t('factors.driverTest.certaintyTest')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      {t('factors.driverTest.significanceTest')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      {t('factors.driverTest.independenceTest')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider w-32">
                      {t('factors.driverTest.result')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {sortedFactors.map(factor => (
                    <tr key={factor.id} className="hover:bg-neutral-50">
                      {/* Factor Info */}
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-neutral-200 rounded-full flex items-center justify-center text-sm font-medium text-neutral-600 mr-3">
                            {factor.name.charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-neutral-900">{factor.name}</div>
                            <div className="text-sm text-neutral-500">{getTranslatedCategory(factor.category)}</div>
                          </div>
                        </div>
                      </td>

                      {/* Certainty Test */}
                      <td className="px-6 py-4 text-center">
                        <div className="flex flex-col space-y-1">
                          {getTestOptions('certainty_test').map(option => 
                            renderTestButton(factor, 'certainty_test', option)
                          )}
                        </div>
                      </td>

                      {/* Significance Test */}
                      <td className="px-6 py-4 text-center">
                        <div className="flex flex-col space-y-1">
                          {getTestOptions('significance_test').map(option => 
                            renderTestButton(factor, 'significance_test', option)
                          )}
                        </div>
                      </td>

                      {/* Independence Test */}
                      <td className="px-6 py-4 text-center">
                        <div className="flex flex-col space-y-1">
                          {getTestOptions('independence_test').map(option => 
                            renderTestButton(factor, 'independence_test', option)
                          )}
                        </div>
                      </td>

                      {/* Què és */}
                      <td className="px-6 py-4 text-center">
                        {(() => {
                          const v = veredicte(factor);
                          if (v === 'pending') {
                            return (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-1 text-xs text-neutral-500">
                                <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" />
                                {t('factors.driverTest.pending')}
                              </span>
                            );
                          }
                          return (
                            <span
                              title={t(`factors.driverTest.verdictWhy.${v}`)}
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                                v === 'driver'
                                  ? 'bg-accent-100 text-accent-800'
                                  : 'bg-neutral-100 text-neutral-600'
                              }`}
                            >
                              {t(`factors.driverTest.verdict.${v}`)}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* El resum.

              Abans eren dues caixes: «Drivers qualificats» i «Factors
              desqualificats», aquesta amb una creu vermella. Ara la segona
              agrupa la resta **pel que són** i diu de cadascun per què serveix.
              El cas del dependent és el que més ho demanava: no ha quedat fora,
              torna a la passa 5. */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="rounded-lg border border-neutral-200 bg-white p-5">
              <h4 className="text-sm font-semibold text-neutral-900">
                {t('factors.driverTest.driversTitle', { count: qualifiedFactors.length })}
              </h4>
              <p className="mt-0.5 text-xs text-neutral-500">
                {t('factors.driverTest.driversLead')}
              </p>
              {qualifiedFactors.length === 0 ? (
                <p className="mt-4 text-sm text-neutral-500">{t('factors.driverTest.noDriversYet')}</p>
              ) : (
                <ul className="mt-4 space-y-1.5">
                  {qualifiedFactors.map(factor => (
                    <li key={factor.id} className="flex items-center gap-3 rounded-md bg-neutral-50 px-3 py-2">
                      <span className="text-sm font-medium text-neutral-900">{factor.name}</span>
                      <span className="ml-auto text-xs text-neutral-500">{getTranslatedCategory(factor.category)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-lg border border-neutral-200 bg-white p-5">
              <h4 className="text-sm font-semibold text-neutral-900">
                {t('factors.driverTest.othersTitle', { count: altresFactors.length })}
              </h4>
              <p className="mt-0.5 text-xs text-neutral-500">
                {t('factors.driverTest.othersLead')}
              </p>
              {altresFactors.length === 0 ? (
                <p className="mt-4 text-sm text-neutral-500">{t('factors.driverTest.noOthersYet')}</p>
              ) : (
                <div className="mt-4 space-y-4">
                  {['trend', 'marginal', 'secondary'].map(v => {
                    const grup = altresFactors.filter(f => veredicte(f) === v);
                    if (!grup.length) return null;
                    return (
                      <div key={v}>
                        <p className="text-xs font-semibold text-neutral-700">
                          {t(`factors.driverTest.verdict.${v}`)}
                        </p>
                        <p className="mt-0.5 text-xs leading-relaxed text-neutral-500">
                          {t(`factors.driverTest.verdictWhy.${v}`)}
                        </p>
                        <ul className="mt-2 space-y-1.5">
                          {grup.map(factor => (
                            <li key={factor.id} className="flex items-center gap-3 rounded-md bg-neutral-50 px-3 py-2">
                              <span className="text-sm text-neutral-900">{factor.name}</span>
                              <span className="ml-auto text-xs text-neutral-500">{getTranslatedCategory(factor.category)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Help Modal */}
          {showHelpModal && (
            <div className="fixed inset-0 bg-neutral-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg w-full max-w-2xl">
                <div className="p-6 border-b border-neutral-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-neutral-900">
                      {t('factors.driverTest.helpTitle')}
                    </h3>
                    <button
                      onClick={() => setShowHelpModal(false)}
                      className="text-neutral-400 hover:text-neutral-600 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="prose prose-sm max-w-none text-neutral-700">
                    <p className="mb-4">{t('factors.driverTest.stepIntroduction')}</p>
                    
                    <div className="bg-neutral-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-neutral-900 mb-3">{t('factors.driverTest.testingCriteria')}:</h4>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-neutral-300 font-mono text-[10px] font-bold text-neutral-500">1</span>
                          <div>
                            <strong className="text-sm text-neutral-900">{t('factors.driverTest.certainty')}:</strong>
                            <p className="text-sm text-neutral-600 mt-1">{t('factors.driverTest.certaintyCriteria')}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-neutral-300 font-mono text-[10px] font-bold text-neutral-500">2</span>
                          <div>
                            <strong className="text-sm text-neutral-900">{t('factors.driverTest.significance')}:</strong>
                            <p className="text-sm text-neutral-600 mt-1">{t('factors.driverTest.significanceCriteria')}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-neutral-300 font-mono text-[10px] font-bold text-neutral-500">3</span>
                          <div>
                            <strong className="text-sm text-neutral-900">{t('factors.driverTest.independence')}:</strong>
                            <p className="text-sm text-neutral-600 mt-1">{t('factors.driverTest.independenceCriteria')}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 rounded-lg border border-neutral-200 bg-white p-3">
                        <p className="text-sm text-neutral-700">
                          <strong>{t('factors.driverTest.important')}:</strong> {t('factors.driverTest.qualificationNote')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
                  <button
                    onClick={() => setShowHelpModal(false)}
                    className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 transition-colors"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DriverTestStep;